import React from 'react';
import ReactMarkdown from 'react-markdown';
import { safeMarkdownProps } from '../../lib/safeMarkdown';
import remarkGfm from 'remark-gfm';
import { Highlight, themes } from 'prism-react-renderer';
import { Box, Code, Group, Button, CopyButton, Tooltip } from '@mantine/core';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import { RichMessageRendererProps } from '../../types/assistant';
import classes from './RichMessageRenderer.module.css';

/**
 * RichMessageRenderer Component
 * Renders markdown content with syntax highlighting
 * Supports code blocks, links, lists, and other markdown features
 */
export const RichMessageRenderer: React.FC<RichMessageRendererProps> = ({
  content,
  role,
}) => {
  return (
    <Box className={classes.richMessageRenderer}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        {...safeMarkdownProps}
        components={{
          // Paragraphs
          p: ({ children }) => (
            <p className={classes.paragraph}>{children}</p>
          ),

          // Headings
          h1: ({ children }) => (
            <h1 className={classes.heading1}>{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className={classes.heading2}>{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className={classes.heading3}>{children}</h3>
          ),

          // Lists
          ul: ({ children }) => (
            <ul className={classes.unorderedList}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className={classes.orderedList}>{children}</ol>
          ),
          li: ({ children }) => (
            <li className={classes.listItem}>{children}</li>
          ),

          // Code blocks
          code: ({ className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : 'text';
            const isInline = !className;

            if (isInline) {
              return (
                <Code className={classes.inlineCode}>
                  {children}
                </Code>
              );
            }

            return (
              <Highlight theme={themes.dracula} code={String(children).trim()} language={language}>
                {({ className: highlightClassName, style, tokens, getTokenProps }) => (
                  <Box className={classes.codeBlockWrapper}>
                    <div className={classes.codeBlockHeader}>
                      <span className={classes.languageBadge}>{language}</span>
                      <CopyButton value={String(children)} timeout={2000}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? 'Kopiert' : 'Code kopieren'} withArrow>
                            <Button
                              color={copied ? 'green' : 'gray'}
                              size="xs"
                              variant="subtle"
                              onClick={copy}
                              rightSection={
                                copied ? (
                                  <IconCheck size={14} />
                                ) : (
                                  <IconCopy size={14} />
                                )
                              }
                            >
                              {copied ? 'Kopiert' : 'Kopieren'}
                            </Button>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </div>
                    <pre className={`${highlightClassName} ${classes.codeBlock}`} style={style}>
                      {tokens.map((line, i) => (
                        <div key={i} className={classes.codeLine}>
                          <span className={classes.lineNumber}>{i + 1}</span>
                          {line.map((token, key) => (
                            <span key={key} {...getTokenProps({ token, key })} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  </Box>
                )}
              </Highlight>
            );
          },

          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className={classes.link}
            >
              {children}
            </a>
          ),

          // Block quotes
          blockquote: ({ children }) => (
            <blockquote className={classes.blockquote}>
              {children}
            </blockquote>
          ),

          // Horizontal rule
          hr: () => <hr className={classes.horizontalRule} />,

          // Tables
          table: ({ children }) => (
            <div className={classes.tableWrapper}>
              <table className={classes.table}>{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className={classes.tableHead}>{children}</thead>,
          tbody: ({ children }) => <tbody className={classes.tableBody}>{children}</tbody>,
          tr: ({ children }) => <tr className={classes.tableRow}>{children}</tr>,
          th: ({ children }) => <th className={classes.tableHeader}>{children}</th>,
          td: ({ children }) => <td className={classes.tableData}>{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
};

RichMessageRenderer.displayName = 'RichMessageRenderer';
